import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ScheduleTime } from "../../shared/db";
import {
  ScheduleFormValidationErrorMessages,
  ScheduleFormValidator,
  ScheduleTimeHelper,
} from "../../model/schedule";
import {
  DefaultTaskScheduleSettingsTextStyle,
  TaskScheduleSettingsDurationInput,
  TaskScheduleSettingsRelativeDayInput,
  TaskScheduleSettingsTimeInput,
} from "./task_schedule_settings_input";
import { useSelectTheme } from "../../store";
import { maybeSoftTry, RIF } from "../../lib";
import { useMount } from "react-use";
import { registerName } from "../../lib/form";
import { isNil } from "lodash";

interface TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeForm {
  day: number;
  startTime: string;
  startTimeHours: number;
  startTimeMinutes: number;
  endTime: string;
  endTimeHours: number;
  endTimeMinutes: number;
  emaExpirationSeconds: number;
  emaExpirationHours: number;
  emaExpirationMinutes: number;
}

type TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeOutput = Omit<
  TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeForm,
  | "startTimeHours"
  | "startTimeMinutes"
  | "endTimeHours"
  | "endTimeMinutes"
  | "emaExpirationHours"
  | "emaExpirationMinutes"
>;

export interface TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeProps {
  values: Partial<TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeOutput>;
  onValidated: (form: TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeOutput) => void;
  onError: () => void;
}

export const TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelative = (
  props: TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeProps,
) => {
  const { values, onValidated, onError } = props;

  const { color, fontSize, fontWeight } = useSelectTheme();

  const textStyle = DefaultTaskScheduleSettingsTextStyle({ color, fontSize, fontWeight });

  const defaultStartTime = values.startTime ?? "08:00";
  const defaultStartTimeParsed = maybeSoftTry(() => ScheduleTime.parse(defaultStartTime));

  const defaultEndTime = !isNil(values.endTime)
    ? ScheduleTimeHelper.coerceToSingleDay(values.endTime)
    : "17:00";
  const defaultEndTimeParsed = maybeSoftTry(() => ScheduleTime.parse(defaultEndTime));

  const defaultEmaExpirationSeconds = values.emaExpirationSeconds ?? 1800;

  const {
    register,
    control,
    formState: { isValidating, isValid, errors },
    trigger,
    setValue,
    getValues,
  } = useForm<TaskScheduleSettingsOneTimeOnlyFullyRandomizedEMARelativeForm>({
    mode: "onChange",
    defaultValues: {
      day: values.day ?? 1,
      startTime: defaultStartTime,
      startTimeHours: defaultStartTimeParsed?.hours ?? 8,
      startTimeMinutes: defaultStartTimeParsed?.minutes ?? 0,
      endTime: defaultEndTime,
      endTimeHours: defaultEndTimeParsed?.hours ?? 17,
      endTimeMinutes: defaultEndTimeParsed?.minutes ?? 0,
      emaExpirationSeconds: defaultEmaExpirationSeconds,
      emaExpirationHours: ScheduleTimeHelper.getHoursFromTotalSeconds(defaultEmaExpirationSeconds),
      emaExpirationMinutes: ScheduleTimeHelper.getMinutesFromTotalSeconds(
        defaultEmaExpirationSeconds,
      ),
    },
  });

  const [latestErrorMessage, setLatestErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if (error?.message) {
        setLatestErrorMessage(error.message);
        onError();
        return;
      }
    }
    setLatestErrorMessage(null);
  }, [
    errors.day,
    errors.startTime,
    errors.startTimeHours,
    errors.startTimeMinutes,
    errors.endTime,
    errors.endTimeHours,
    errors.endTimeMinutes,
    errors.emaExpirationSeconds,
    errors.emaExpirationHours,
    errors.emaExpirationMinutes,
  ]);

  useMount(() => {
    trigger();
  });

  useEffect(() => {
    if (isValid && !isValidating) {
      const form = getValues();
      onValidated(form);
    }
  }, [isValid, isValidating]);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div>
        <span css={{ ...textStyle }}>The task will appear randomly on</span>
        <TaskScheduleSettingsRelativeDayInput
          register={registerName(register, "day")}
          failedValidation={!!errors.day}
          {...{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Day"),
            },
            min: {
              value: 1,
              message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber("Day"),
            },
          }}
        />
        <span css={{ ...textStyle }}>between</span>
        <input
          type="hidden"
          {...register("startTime", {
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Time"),
            },
            pattern: {
              value: ScheduleTime.REGEX,
              message: ScheduleFormValidationErrorMessages.timeInvalid("Start Time"),
            },
            validate: {
              validDateTimeInterval: (value) => {
                const { success } = ScheduleFormValidator.validateTimeInterval({
                  startTime: value,
                  endTime: getValues("endTime"),
                });
                return success
                  ? true
                  : ScheduleFormValidationErrorMessages.shouldBeLessThan("Start Time", "End Time");
              },
            },
            deps: ["endTime"],
          })}
        />
        <TaskScheduleSettingsTimeInput
          control={control}
          hoursName={"startTimeHours"}
          minutesName={"startTimeMinutes"}
          failedValidation={
            !!errors.startTimeHours || !!errors.startTimeMinutes || !!errors.startTime
          }
          hoursRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Hours"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.hourInvalid("Start Hours"),
            },
            max: {
              value: 23,
              message: ScheduleFormValidationErrorMessages.hourInvalid("Start Hours"),
            },
            onChange: (e) => {
              const startTimeHours = parseInt(e.target.value);
              setValue("startTimeHours", startTimeHours);
              const startTimeMinutes = getValues("startTimeMinutes");
              if (!isNil(startTimeMinutes)) {
                setValue("startTime", ScheduleTimeHelper.format(startTimeHours, startTimeMinutes));
              }
              trigger(["startTimeMinutes", "startTimeHours", "startTime", "endTime"]);
            },
          }}
          minutesRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Start Minutes"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("Start Minutes"),
            },
            max: {
              value: 59,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("Start Minutes"),
            },
            onChange: (e) => {
              const startTimeMinutes = parseInt(e.target.value);
              setValue("startTimeMinutes", startTimeMinutes);
              const startTimeHours = getValues("startTimeHours");
              if (!isNil(startTimeHours)) {
                setValue("startTime", ScheduleTimeHelper.format(startTimeHours, startTimeMinutes));
              }
              trigger(["startTimeMinutes", "startTimeHours", "startTime", "endTime"]);
            },
          }}
        />
        <span css={{ ...textStyle }}>to</span>
        <input
          type="hidden"
          {...register("endTime", {
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Time"),
            },
            pattern: {
              value: ScheduleTime.REGEX,
              message: ScheduleFormValidationErrorMessages.timeInvalid("End Time"),
            },
            validate: {
              validDateTimeInterval: (value) => {
                const { success } = ScheduleFormValidator.validateTimeInterval({
                  startTime: getValues("startTime"),
                  endTime: value,
                });
                return success
                  ? true
                  : ScheduleFormValidationErrorMessages.shouldBeGreaterThan(
                      "End Time",
                      "Start Time",
                    );
              },
            },
            deps: ["startTime"],
          })}
        />
        <TaskScheduleSettingsTimeInput
          control={control}
          hoursName={"endTimeHours"}
          minutesName={"endTimeMinutes"}
          failedValidation={!!errors.endTimeHours || !!errors.endTimeMinutes || !!errors.endTime}
          hoursRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Hours"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.hourInvalid("End Hours"),
            },
            max: {
              value: 23,
              message: ScheduleFormValidationErrorMessages.hourInvalid("End Hours"),
            },
            onChange: (e) => {
              const endTimeHours = parseInt(e.target.value);
              setValue("endTimeHours", endTimeHours);
              const endTimeMinutes = getValues("endTimeMinutes");
              if (!isNil(endTimeMinutes)) {
                setValue("endTime", ScheduleTimeHelper.format(endTimeHours, endTimeMinutes));
              }
              trigger(["endTimeHours", "endTimeMinutes", "endTime", "startTime"]);
            },
          }}
          minutesRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("End Minutes"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("End Minutes"),
            },
            max: {
              value: 59,
              message: ScheduleFormValidationErrorMessages.minuteInvalid("End Minutes"),
            },
            onChange: (e) => {
              const endTimeMinutes = parseInt(e.target.value);
              setValue("endTimeMinutes", endTimeMinutes);
              const endTimeHours = getValues("endTimeHours");
              if (!isNil(endTimeHours)) {
                setValue("endTime", ScheduleTimeHelper.format(endTimeHours, endTimeMinutes));
              }
              trigger(["endTimeMinutes", "endTimeHours", "endTime", "startTime"]);
            },
          }}
        />
      </div>
      <div
        css={{
          height: "1px",
          backgroundColor: color.border.default,
          width: "100%",
        }}
      />
      <div>
        <span css={{ ...textStyle }}>The task will remains visible for</span>
        <input
          type="hidden"
          {...register("emaExpirationSeconds", {
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Expiration"),
            },
            min: {
              value: 1,
              message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber("Expiration"),
            },
          })}
        />
        <TaskScheduleSettingsDurationInput
          control={control}
          hoursName={"emaExpirationHours"}
          minutesName={"emaExpirationMinutes"}
          failedValidation={
            !!errors.emaExpirationHours ||
            !!errors.emaExpirationMinutes ||
            !!errors.emaExpirationSeconds
          }
          hoursRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Expiration Hours"),
            },
            min: {
              value: 0,
              message: ScheduleFormValidationErrorMessages.durationHourInvalid("Expiration Hours"),
            },
            onChange: (e) => {
              const emaExpirationHours = parseInt(e.target.value);
              setValue("emaExpirationHours", emaExpirationHours);
              const emaExpirationMinutes = getValues("emaExpirationMinutes");
              if (!isNil(emaExpirationMinutes)) {
                setValue(
                  "emaExpirationSeconds",
                  ScheduleTimeHelper.getTotalSecondsFromHoursAndMinutes(
                    emaExpirationHours,
                    emaExpirationMinutes,
                  ),
                );
              }
              trigger(["emaExpirationHours", "emaExpirationMinutes", "emaExpirationSeconds"]);
            },
          }}
          minutesRules={{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required("Expiration Minutes"),
            },
            min: {
              value: 0,
              message:
                ScheduleFormValidationErrorMessages.durationMinuteInvalid("Expiration Minutes"),
            },
            max: {
              value: 59,
              message:
                ScheduleFormValidationErrorMessages.durationMinuteInvalid("Expiration Minutes"),
            },
            onChange: (e) => {
              const emaExpirationMinutes = parseInt(e.target.value);
              setValue("emaExpirationMinutes", emaExpirationMinutes);
              const emaExpirationHours = getValues("emaExpirationHours");
              if (!isNil(emaExpirationHours)) {
                setValue(
                  "emaExpirationSeconds",
                  ScheduleTimeHelper.getTotalSecondsFromHoursAndMinutes(
                    emaExpirationHours,
                    emaExpirationMinutes,
                  ),
                );
              }
              trigger(["emaExpirationMinutes", "emaExpirationHours", "emaExpirationSeconds"]);
            },
          }}
        />
      </div>
      {RIF(latestErrorMessage, (message) => (
        <div css={{ ...textStyle, color: color.warning }}>{message}</div>
      ))}
    </div>
  );
};
